import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { CashflowDrawerBody } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer.components';
import { CashflowDrawerFormShape } from '@/modules/cashflows/CashflowDrawer/CashflowDrawer.types';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { CashflowsImportFormShape } from './CashflowsImport.types';

interface CashflowsImportEditDrawerProps {
  cashflowId: string;
  onClose: () => void;
}

export function CashflowsImportEditDrawer({
  cashflowId,
  onClose,
}: CashflowsImportEditDrawerProps) {
  const { householdId } = useManageIntegrationDataContext();
  const { getValues, setValue } = useFormContext<CashflowsImportFormShape>();
  const row = getValues(`cashflows.${cashflowId}`);
  const formMethods = useForm<CashflowDrawerFormShape>({
    defaultValues: useMemo(
      () => ({
        name: row.name,
        targetId: row.targetId,
        valueType: row.valueType,
        amount: row.amount,
        percentage: row.percentage,
        growthRate: row.growthRate,
        frequency: row.frequency,
        startYear: row.startYear,
        endType: row.endType,
        endYear: row.endYear,
        endAtDeathClientProfileId: row.endAtDeathClientProfileId,
        incomeOrExpense: row.incomeOrExpense,
      }),
      [row]
    ),
  });

  const handleSave = useCallback(
    (values: CashflowDrawerFormShape) => {
      setValue(`cashflows.${cashflowId}.targetId`, values.targetId, {
        shouldDirty: true,
      });
      onClose();
    },
    [cashflowId, onClose, setValue]
  );

  return (
    <SideDrawer
      isOpen
      onClose={onClose}
      width={OVERLAY_MD}
      heading="Edit cash flow"
      bodyPropsSx={{ p: 2 }}
      body={
        <FormProvider {...formMethods}>
          <CashflowDrawerBody householdId={householdId} disableImportedFields />
        </FormProvider>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={formMethods.handleSubmit(handleSave)}
          >
            Done
          </Button>
        </Stack>
      }
    />
  );
}
